import React from 'react';
import type { InvestigationPreflight } from '../../lib/chat-codex.js';
import { codexStatusLabel, type CodexReceipt } from '../../lib/codex-receipt.js';
import { MarkdownContent } from './MarkdownContent.js';

export type CodexInvestigationState = InvestigationPreflight | 'checking' | 'uncertain' | 'consent_required' | 'in_progress'
  | 'consent_rate_limited' | 'consent_pending' | 'connected';
export interface CodexInvestigationCardProps {
  state?: CodexInvestigationState;
  receipt?: CodexReceipt;
  onRefresh?: () => void;
  onCancel?: () => void;
  /** Re-enters the SAME start (same key): from the local copy, or via the server's stored copy
   * when this tab has none. Explicit user action only. */
  onCheckAgain?: () => void;
  /** Opens the one-time consent for repository investigation in this chat. */
  onConnect?: () => void;
  /** Asks the server to settle an unconfirmed start (the caller confirms first). Mission Control is
   * checked first: a real run is shown, never discarded. */
  onDiscard?: () => void;
  busy?: boolean;
}

const NOT_REPORTED = 'not reported';
const tokens = (value: number | null | undefined): string => (typeof value === 'number' ? value.toLocaleString() : NOT_REPORTED);
/** Run metering as display text. Unknown (null or absent) is "not reported", never 0. */
export function codexMeteringLines(receipt: CodexReceipt): string[] {
  return [
    `Model: ${receipt.model_id ? receipt.model_id : NOT_REPORTED}`,
    `Tokens: input ${tokens(receipt.input_tokens)}, cached input ${tokens(receipt.cached_input_tokens)}, output ${tokens(receipt.output_tokens)}`,
    `Priced cost: ${typeof receipt.priced_cost_usd === 'number' ? `$${receipt.priced_cost_usd.toFixed(4)}` : NOT_REPORTED}`,
  ];
}

/** Wall seconds as display text ("42s", "3m 05s", "1h 02m 03s"); unknown is "not reported", never 0. */
export function formatCodexDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return NOT_REPORTED;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
  if (m > 0) return `${m}m ${pad(s)}s`;
  return `${s}s`;
}
/** Settlement as display text: the final credit total (and USD when MC reported it) only once MC settled
 * the run; released and refused are terminal outcomes; every other state is pending. Plus the run
 * duration ("not reported" when unknown). */
export function codexSettlementLines(receipt: CodexReceipt): string[] {
  const settled = receipt.settlement_state === 'settled' && receipt.charged_credits !== null;
  let line = 'Final credit total pending';
  if (settled) {
    const usd = receipt.settled_amount ? `, $${receipt.settled_amount.usd.toFixed(4)}` : '';
    line = `${(receipt.charged_credits as number).toLocaleString()} credits charged (final total${usd})`;
  } else if (receipt.settlement_status === 'released') line = 'Reservation released, no charge';
  else if (receipt.settlement_status === 'refused') line = 'Settlement refused, operator review';
  return [line, `Duration: ${formatCodexDuration(receipt.wall_seconds)}`];
}

export function CodexInvestigationCard({ state, receipt, onRefresh, onCancel, onCheckAgain, onConnect, onDiscard, busy = false }: CodexInvestigationCardProps) {
  const copy: Record<CodexInvestigationState, string> = {
    checking: 'Checking repository investigation availability…',
    available: 'Starting repository investigation…',
    unavailable: 'Repository investigation is currently unavailable. Your existing chat is still available.',
    verification_unavailable: 'Could not verify investigation availability. Your existing chat is still available.',
    denied: 'Repository investigation is not permitted for this chat.',
    uncertain: 'The request could not be confirmed. Verify the existing investigation before starting another.',
    consent_required: 'Repository investigation needs your one-time permission for this workspace.',
    in_progress: 'An investigation is already running in this chat. Refresh or stop it before starting another.',
    consent_rate_limited: 'Too many permission requests. Wait a few minutes, then try again.',
    consent_pending: 'A permission request is already open. Finish it in the Mission Control tab, or wait a few minutes and try again.',
    connected: 'Repository investigation is connected. Send your /investigate request again to start it.',
  };
  const canRequestStop = receipt && ['submitted', 'queued', 'running', 'reconciliation_pending'].includes(receipt.status);
  return <section aria-label="Repository investigation" role="status" data-investigation-state={state ?? receipt?.status}
    style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
    <strong>Repository investigation</strong>
    {state && <p style={{ margin: '8px 0 0' }}>{copy[state]}</p>}
    {onCheckAgain && state !== 'checking' && state !== 'available' && state !== 'consent_required'
      && <button type="button" disabled={busy} onClick={onCheckAgain}>Check again</button>}
    {onDiscard && state !== 'checking' && state !== 'available' && state !== 'consent_required'
      && <button type="button" disabled={busy} onClick={onDiscard}>Discard stuck investigation</button>}
    {state === 'consent_required' && onConnect && <button type="button" disabled={busy} onClick={onConnect}>Allow repository investigation</button>}
    {receipt && state !== 'denied' && <>
      <p>{state ? 'Last confirmed status: ' : ''}{codexStatusLabel(receipt)}</p>
      {receipt.result !== null && <MarkdownContent content={receipt.result} />}
      <ul aria-label="Run settlement" style={{ margin: '4px 0', paddingLeft: 18 }}>
        {codexSettlementLines(receipt).map((line: string) => <li key={line}>{line}</li>)}
      </ul>
      <ul aria-label="Run metering" style={{ margin: '4px 0 8px', paddingLeft: 18 }}>
        {codexMeteringLines(receipt).map((line: string) => <li key={line}>{line}</li>)}
      </ul>
      {onRefresh && <button type="button" disabled={busy} onClick={onRefresh}>Refresh status</button>}
      {onCancel && canRequestStop && <button type="button" disabled={busy} onClick={onCancel}>Stop investigation</button>}
    </>}
  </section>;
}
